/* The fixed dashboards (ADR-0195). Tokens only (ADR-0007): no colour literal, no brand green on a
   figure. Layout is a 12-column grid that drops to one column under 1100px; every tile is a panel,
   so a tile's edge, heading and failure state are the product's own. */

.view {
  grid-column: 1 / -1;
  display: grid;
  grid-template-columns: repeat(12, minmax(0, 1fr));
  gap: var(--gap);
  /* Panels that share a row share a top and a bottom (alignment audit, 2026-11-03): the cell stretches
     to the row and its panel fills the cell, instead of each panel stopping where its own text does. */
  align-items: stretch;
  min-width: 0;
}

.span4,
.span5,
.span6,
.span7,
.span8,
.span12 {
  display: flex;
  flex-direction: column;
}

.span4 > *,
.span5 > *,
.span6 > *,
.span7 > *,
.span8 > *,
.span12 > * {
  flex: 1 1 auto;
}

.span4 {
  grid-column: span 4;
  min-width: 0;
}

.span5 {
  grid-column: span 5;
  min-width: 0;
}

.span6 {
  grid-column: span 6;
  min-width: 0;
}

.span7 {
  grid-column: span 7;
  min-width: 0;
}

.span8 {
  grid-column: span 8;
  min-width: 0;
}

.span12 {
  grid-column: 1 / -1;
  min-width: 0;
}

@media (width <= 1100px) {
  .span4, .span5, .span6, .span7, .span8 { grid-column: 1 / -1; }
}

.screen {
  grid-column: 1 / -1;
  display: grid;
  align-content: start;
  gap: var(--gap);
  min-width: 0;
}

.area {
  grid-column: 1 / -1;
  display: grid;
  gap: var(--gap);
  min-width: 0;
  outline-offset: 2px;
}

.about {
  margin: 0;
  color: var(--muted);
  font-size: var(--t-dense);
}

/* A row of label, mark and figure, used by every list tile.

   **The rows share three column tracks** (alignment audit, 2026-11-03). Each row used to be its own
   grid with an `auto` last column, so a longer figure or chip in one row moved that row's middle
   column left of the row above it: the status chips of three filings started at three different x.
   The list owns the tracks and each row, and the item that holds it, is a subgrid of them, so every
   row's columns start and end on the same lines. The first declaration is the fallback for a
   browser without `subgrid`: the per-row grid it replaces. */
.rows {
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  grid-template-columns: minmax(0, 1.4fr) minmax(0, 1fr) max-content;
  gap: 2px 10px;
}

.rows > li {
  display: grid;
  grid-template-columns: minmax(0, 1.4fr) minmax(0, 1fr) auto;
  grid-template-columns: subgrid;
  grid-column: 1 / -1;
}

/* The row itself, and anything under it inside the item (the "before" link), spans the tracks. */
.rows > li > * {
  grid-column: 1 / -1;
}

.row {
  display: grid;
  grid-template-columns: minmax(0, 1.4fr) minmax(0, 1fr) auto;
  grid-template-columns: subgrid;
  align-items: center;
  column-gap: 10px;
  min-height: var(--target-min);
  padding: 4px 0;
  font-size: var(--t-dense);
  color: var(--ink);
  border-top: 1px solid var(--line);
}

.row:first-child { border-top: 0; }

/* The tab's one table (Executive: what is waiting on someone, a count each). */
.counts {
  width: 100%;
  border-collapse: collapse;
  font-size: var(--t-dense);
}

.counts th,
.counts td {
  min-height: var(--target-min);
  padding: 6px 4px;
  border-top: 1px solid var(--line);
  text-align: left;
}

.counts thead th {
  border-top: 0;
  color: var(--muted);
  font-weight: 600;
}

.counts .figure {
  text-align: right;
}

.rowLink {
  text-decoration: none;
  color: var(--ink);
}

.rowLink:hover .name { text-decoration: underline; }

.rowLink:focus-visible {
  outline: 2px solid var(--focus);
  outline-offset: -2px;
  border-radius: 2px;
}

.name {
  min-width: 0;
  overflow-wrap: anywhere;
}

.sub {
  display: block;
  color: var(--muted);
  font-size: var(--t-min);
}

.figure {
  font-variant-numeric: lining-nums tabular-nums;
  font-weight: 600;
  text-align: right;
}

.words {
  color: var(--muted);
  font-weight: 400;
}

.track {
  display: block;
  height: 8px;
  border-radius: 4px;
  background: var(--panel-3);
  overflow: hidden;
}

.fill {
  display: block;
  height: 100%;
  border-radius: 4px;
  background: var(--ink-2);
  transform-origin: left center;
}

.fillScope1 { background: var(--s1); }
.fillScope2 { background: var(--s2); }
.fillScope3 { background: var(--s3); }
.fillInfo { background: var(--info); }
.fillWarn { background: var(--warn); }
.fillMuted { background: var(--muted); }

/* A bar of parts (a site's scopes): as long as a plain bar, split by each part's weight, with a 1px
 * gap so neighbours are told apart by more than hue. */
.stack {
  display: flex;
  height: 100%;
  gap: 1px;
  border-radius: 4px;
  overflow: hidden;
}

.part {
  display: block;
  min-width: 2px;
  flex-basis: 0;
}

/* A mark that is a recorded absence, drawn as an outline and never as a short bar. */
.outline {
  display: block;
  height: 8px;
  border-radius: 4px;
  border: 1px dashed var(--line-2);
}

.note {
  margin: 10px 0 0;
  color: var(--muted);
  font-size: var(--t-min);
  line-height: 1.5;
}

.notShown {
  color: var(--muted);
  font-weight: 400;
  font-size: var(--t-min);
}

.pair {
  display: grid;
  gap: 3px;
}

/* Used on a <button> as well as a link: without the resets the button keeps the browser's own face,
   which a dark colour-scheme paints #6b6b6b and which put the accent at 2.75:1 (found by the F6
   dark sweep). */
.link {
  padding: 0;
  border: 0;
  background: none;
  cursor: pointer;
  color: var(--accent);
  text-decoration: underline;
  text-underline-offset: 2px;
  font-size: var(--t-dense);
}

.link:hover { color: var(--accent-h); }

.link:focus-visible {
  outline: 2px solid var(--focus);
  outline-offset: 2px;
  border-radius: 2px;
}

.foot {
  margin-top: 10px;
  display: flex;
  flex-wrap: wrap;
  gap: 10px 16px;
}

.big {
  font-family: var(--font);
  font-size: var(--t-kpi);
  font-weight: 650;
  line-height: 1.05;
  font-variant-numeric: lining-nums tabular-nums;
  color: var(--ink);
}

.bigUnit {
  font-family: var(--font);
  font-size: var(--t-dense);
  color: var(--muted);
  margin-left: 4px;
}

.tableFallback {
  margin-top: 8px;
  font-size: var(--t-min);
}

.tableFallback summary {
  cursor: pointer;
  color: var(--muted);
  min-height: var(--target-min);
  display: flex;
  align-items: center;
}

.tableFallback table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 6px;
}

.tableFallback th,
.tableFallback td {
  text-align: left;
  padding: 4px 6px;
  border-top: 1px solid var(--line);
  font-weight: 400;
}

.tableFallback td:last-child,
.tableFallback th:last-child {
  text-align: right;
  font-variant-numeric: lining-nums tabular-nums;
}

.paired {
  display: grid;
  gap: 3px;
}

.pairedTrack {
  display: grid;
  gap: 3px;
}

.empty {
  margin: 0;
  color: var(--muted);
  font-size: var(--t-dense);
  line-height: 1.5;
}

.tally {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(110px, 1fr));
  gap: 8px;
  margin-bottom: 10px;
}

.tallyCell {
  border: 1px solid var(--line);
  border-radius: var(--radius-ctl);
  padding: 6px 8px;
}

.tallyCell strong {
  display: block;
  font-size: var(--t-head);
  font-variant-numeric: lining-nums tabular-nums;
}

.tallyCell span {
  color: var(--muted);
  font-size: var(--t-min);
}

@media (prefers-reduced-motion: no-preference) {
  .fill { transition: transform var(--dur-4) var(--ease-out); }
}
